import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import { ActivateIcon, DeactivateFileIcon, FileContractIcon, PlusIcon } from '@repo/ui/icons'
import { useMutation } from '@tanstack/react-query'
import React from 'react'

import { API } from '../../../api/apis.config'
import { activateContractTemplate } from '../../../api/contract-template/activate-contract-template'
import { deactivateContractTemplate } from '../../../api/contract-template/deactivate-contract-template'
import CountryFlag from '../../../components/elements/country-flag'
import DynamicDataTable, { DynamicTableRef } from '../../../components/elements/dynamic-table'
import UserNavBar from '../../../components/elements/nav-bar'
import { QualificationTags } from '../../../components/elements/qualification-badge'
import { Paths } from '../../../lib/constants/path'
import useTranslate from '../../../lib/hooks/use-translate'
import { staffPositionEnumMapping } from '../../../lib/types/enums/staff/staff-position.enum'
import { ContractTemplate } from '../../../lib/types/models/contract-template/contract-template'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'

export function ContractTemplateListPage() {
  const { navigate, navigateWithEvent } = useNavigator()
  const { t } = useTranslate()

  const tableRef = React.useRef<DynamicTableRef<ContractTemplate>>(null)
  const columns: Column<ContractTemplate>[] = [
    {
      enableSorting: true,
      accessorKey: 'name',
      header: 'Noms des Modèles'
    },
    {
      enableSorting: true,
      accessorKey: 'isActive',
      header: 'Statut',
      sortDescFirst: true,
      invertSorting: true,
      cell: ({ row }) => (
        <Badge
          text={row.original.isActive ? 'Actif' : 'Inactif'}
          variant={row.original.isActive ? 'success' : 'danger'}
        />
      )
    },
    {
      enableSorting: true,
      accessorKey: 'staffType',
      header: 'Poste',
      cell: ({ row }) => t(staffPositionEnumMapping[row.original.staffType])
    },
    {
      accessorKey: 'qualifications',
      header: 'Qualifications',
      cell: ({ row }) =>
        row.original.staffQualifications.length === 0 ? (
          <span>-</span>
        ) : (
          <QualificationTags qualifications={row.original.staffQualifications} />
        )
    },
    {
      accessorKey: 'countries',
      header: 'Pays',
      cell: ({ row }) => {
        return !row.original.countries.length ? (
          <span>-</span>
        ) : (
          <div className={'flex py-3 gap-2'}>
            {row.original.countries.map((country, index) => (
              <CountryFlag key={index} possibleCountry={country.name} code={country.code} />
            ))}
          </div>
        )
      }
    }
  ]

  const { mutateAsync: activateContractTemplateMutation } = useMutation({
    mutationFn: activateContractTemplate,
    onError: showAxiosError
  })

  const { mutateAsync: deactivateContractTemplateMutation } = useMutation({
    mutationFn: deactivateContractTemplate,
    onError: showAxiosError
  })

  return (
    <div>
      <UserNavBar title={'Gestion des Modèles de Contrats'} icon={FileContractIcon} />
      <div className={'h-4'} />

      <DynamicDataTable<ContractTemplate>
        ref={tableRef}
        title={'Liste des modèles'}
        deleteMessage={{
          name: 'modèles de contrat',
          attributeName: 'name',
          deleteOne: (contractTemplate) =>
            `Êtes-vous sûr de vouloir supprimer le modèle de contrat "${contractTemplate.name}" ?`,
          deleteMany: (contractTemplates) =>
            `Êtes-vous sûr de vouloir supprimer ces modèles de contrat "${contractTemplates
              .map((ct) => ct.name)
              .join('", "')}" ?`
        }}
        columns={columns}
        selectable
        version={'FULL'}
        allowSearchParam
        onRowClick={(row, event) =>
          navigateWithEvent(
            Paths.CONTRACT_TEMPLATE_UPDATE_PAGE.replace(':id', row.id.toString()),
            event
          )
        }
        endpoint={API.contractTemplate.getAll.url}
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            label: 'Créer un nouveau modèle',
            fn: () => navigate(Paths.CONTRACT_TEMPLATE_CREATE_PAGE)
          },
          {
            type: 'multiple',
            icon: ActivateIcon,
            label: 'Réactiver',
            variant: 'ghost',
            refetch: true,
            confirmationMessage: 'Êtes-vous sûr de vouloir réactiver le contrat ?',
            requiredConfirmation: true,
            cancelText: 'Fermer',
            confirmText: 'Réactiver',
            confirmationIcon: ActivateIcon,
            fn: (staffs) => activateContractTemplateMutation(staffs.map((data) => data.id))
          },
          {
            type: 'multiple',
            icon: DeactivateFileIcon,
            label: 'Désactiver',
            confirmationIcon: DeactivateFileIcon,
            variant: 'destructive',
            refetch: true,
            cancelText: 'Fermer',
            hideButtonIcon: true,
            confirmText: 'Désactiver',
            confirmationMessage: 'Êtes-vous sûr de vouloir désactiver ce modèle de contrat ?',
            requiredConfirmation: true,
            fn: (staffs) => deactivateContractTemplateMutation(staffs.map((data) => data.id))
          }
        ]}
      />
    </div>
  )
}
